import {
  CSNotification,
  NotificationChannel,
  BulkReadNotificationsRequest,
} from "@today/api/tracker"
import { useSWRWithAuth, SWR_AUTH_TOKEN_HEADER } from "@today/api"
import { useAuthentication } from "@today/auth"
import { useCallback } from "react"
import axios from "axios"

const POLLING_INTERVAL = 30_000 // 30초

export function useNotificationChannel() {
  const { isAuthenticated } = useAuthentication()

  const { data, mutate } = useSWRWithAuth<NotificationChannel>(
    isAuthenticated ? "/api/notifications/channels/cs" : null,
    { refreshInterval: POLLING_INTERVAL }
  )

  const unreadCount = data?.unreadCount ?? 0

  return { channel: data, unreadCount, mutateChannel: mutate }
}

/**
 * partner 는 **CS 채널만** 다룬다 — 조회 경로가 `/channels/cs/...` 로 고정돼 있다.
 *
 * `@today/api` 의 `Notification` 은 CS 와 이슈 v2(SW-1406) 를 합친 유니온이지만, 이슈 채널은
 * 사내 그룹(본사CS·고객센터·정류소·협력사)에게만 발송되므로 화주·SME 계정에는 오지 않는다.
 * 그래서 여기서 `CSNotification` 으로 좁혀 소비처가 `channel` 분기를 하지 않아도 되게 한다.
 */
export function useNotificationList(options?: { unread?: boolean }) {
  const { isAuthenticated } = useAuthentication()
  const unreadFilter = options?.unread

  const { data, error, mutate } = useSWRWithAuth<{
    notifications: CSNotification[]
    totalCount: number
    nextPageToken?: string
  }>(
    isAuthenticated
      ? `/api/notifications/channels/cs/notifications?pageSize=20${
          unreadFilter ? "&unread=true" : ""
        }`
      : null,
    { revalidateOnFocus: false }
  )

  return {
    notifications: data?.notifications ?? [],
    totalCount: data?.totalCount ?? 0,
    isLoading: !data && !error,
    error,
    mutateNotifications: mutate,
  }
}

export function useBulkReadNotifications() {
  const { accessToken } = useAuthentication()

  const bulkRead = useCallback(
    async (body: BulkReadNotificationsRequest) => {
      if (!accessToken) return
      await axios.post("/api/notifications", body, {
        headers: { [SWR_AUTH_TOKEN_HEADER]: accessToken },
      })
    },
    [accessToken]
  )

  return { bulkRead }
}
